import React from 'react';
import { isMobile } from 'react-device-detect';
import styled from '@emotion/styled';
import breakpoints from '../../../theme/material/breakpoints';
import colors from '../../../theme/colors';
import { getBackgroundTheme, getBorders, getTextColor, getTextType } from '../../../style';
import { isDarkMode } from '../../../utils/helpers';
import Button from '../../../atoms/buttons/Button';

export const HeadlineContainer = styled('div')<{ isJoinFlow?: boolean }>(({ isJoinFlow }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  marginTop: '-10px',
  width: isJoinFlow ? '100%' : 345,
  [breakpoints.down('md')]: {
    marginTop: '-5px',
  },
}));

export const Headline = styled('h1')(() => ({
  margin: '15px 0 0',
  display: 'inline-block',
  color: colors.light.blue.primary,
  fontSize: 24,
  fontWeight: 700,
  letterSpacing: '-0.48px',
}));

export const QRCodeContainer = styled('div')({
  margin: '20px 0 40px 0',
  width: '100%',
  textAlign: 'center',
  minHeight: 300,
  justifyContent: 'center',
  alignItems: 'center',
  display: 'flex',
  flexDirection: 'column',
});

export const qrCodeWrraper = {
  height: 'auto',
  maxWidth: '230px',
  width: '230px',
  marginBottom: 20,
};

export const Title = styled('h1')(() => ({
  margin: 0,
  display: 'block',
  fontSize: 20,
  letterSpacing: '-0.4px',
  ...getTextType().blockHeader,
}));

export const SuccessTitle = styled('p')(() => ({
  margin: 0,
  display: 'block',
  fontSize: 22,
  fontWeight: 600,
  lineHeight: '20px',
  letterSpacing: '-0.44px',
  ...getTextColor().SUCCESS,
}));

export const StepDescription = styled('p')<{ isJoinFlow: boolean }>(({ isJoinFlow }) => ({
  display: 'inline-block',
  fontSize: 16,
  fontWeight: 400,
  color: isJoinFlow ? colors.light.gray.light : getTextColor().LIGHT_GRAY.color,
  textAlign: 'center',
  margin: '15px 0px 20px 0px',
  letterSpacing: 'normal',
}));

export const ConfirmDescription = styled('p')({
  display: 'inline-block',
  fontSize: 15,
  fontWeight: 700,
  textAlign: 'center',
  margin: '15px 0px 0px 0px',
  lineHeight: '20px',
  letterSpacing: 'normal',
  ...getTextColor().LIGHT_GRAY,
});

export const StyledContainer = styled('div')({
  marginTop: 'unset',
});

export const HelpContainer = styled('div')<{ customStyle?: React.CSSProperties }>(({ customStyle }) => ({
  background: isDarkMode() ? '#113956' : 'rgba(234, 244, 251, 0.50)',
  borderRadius: 8,
  display: 'flex',
  padding: '20px 16px',
  flexDirection: 'row',
  alignItems: 'flex-start',
  gap: 0,
  alignSelf: 'stretch',
  ...customStyle,
}));

export const NotesTitle = styled('p')<{ isJoinFlow: boolean }>(({ isJoinFlow }) => ({
  color: isJoinFlow || !isDarkMode() ? '#2B8FD7' : '#71B5E4',
  fontSize: 18,
  fontWeight: 600,
  lineHeight: '21px',
  margin: 0,
}));

export const NotesSubtitle = styled('p')<{ isJoinFlow: boolean }>(({ isJoinFlow }) => ({
  color: isJoinFlow ? colors.light.gray.light : getTextColor().WHITE_GRAY.color,
  fontSize: '15px',
  fontWeight: 400,
  lineHeight: '21px',
  margin: '5px 0 0 0',
}));

export const ThankYouDescription = styled('p')({
  display: 'inline-block',
  fontSize: 16,
  fontWeight: 500,
  color: colors.light.gray.light,
  textAlign: 'center',
  margin: '5px 0px 10px 0px',
  lineHeight: '21px',
  letterSpacing: '-0.32px',
});

const overlayStyle = {
  overlay: {
    zIndex: 10001,
  },
};

export const cameraPermissionModalStyle = {
  content: {
    borderRadius: '10px',
    width: isMobile ? 325 : 650,
    maxHeight: isMobile ? '80%' : '80%',
    padding: isMobile ? '40px 20px' : '40px 50px 20px 50px',
    backgroundColor: getBackgroundTheme().block2,
    border: getBorders().DEFAULT,
    top: isMobile ? 30 : 65,
  },
  ...overlayStyle,
};

export const selfieWallModalStyle = {
  content: {
    borderRadius: '10px',
    maxWidth: isMobile ? 345 : 400,
    padding: isMobile ? '40px 20px' : '40px 50px',
    backgroundColor: getBackgroundTheme().fsModal,
    border: getBorders().DEFAULT,
    marginTop: 100,
  },
  ...overlayStyle,
};

export const confirmModalStyle = {
  content: {
    borderRadius: '10px',
    maxWidth: isMobile ? 305 : 398,
    height: isMobile ? 'auto' : 398,
    padding: isMobile ? '60px 20px 30px 20px' : '40px 50px 20px 50px',
    backgroundColor: getBackgroundTheme().block2,
    border: getBorders().DEFAULT,
    top: 100,
  },
  ...overlayStyle,
};

export const selfieWallContentStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  height: '100%',
};

export const commonModalStyle = {
  content: {
    borderRadius: '10px',
    maxWidth: isMobile ? 305 : 398,
    padding: isMobile ? '20px 20px 30px 20px' : '20px 50px 20px 50px',
    backgroundColor: getBackgroundTheme().block2,
    border: getBorders().DEFAULT,
    top: 100,
  },
  ...overlayStyle,
};

export const Description = styled('p')<{ isJoinFlow?: boolean; customStyle?: React.CSSProperties }>(
  ({ isJoinFlow, customStyle }) => ({
    display: 'inline-block',
    fontSize: 15,
    fontWeight: 400,
    color: isJoinFlow ? colors.light.gray.light : getTextColor().LIGHT_GRAY.color,
    textAlign: 'center',
    margin: '20px 0px 0px 0px',
    lineHeight: 'normal',
    letterSpacing: '-0.3px',
    maxWidth: 295,
    ...customStyle,
  })
);

export const tryAgainModalStyle = {
  content: {
    position: 'absolute',
    borderRadius: '20px',
    backgroundColor: getBackgroundTheme().block2,
    border: getBorders().DEFAULT,
    width: isMobile ? 305 : 400,
    minHeight: 390,
    height: 'auto',
    padding: isMobile ? '60px 20px 30px 20px' : '40px 50px 30px 50px',
    top: isMobile ? 50 : 70,
  },
  ...overlayStyle,
};

export const TryAgainHeadline = styled('p')<{ isJoinFlow: boolean }>(({ isJoinFlow }) => ({
  margin: 0,
  display: 'inline-block',
  color: isJoinFlow ? colors.light.blue.primary : getTextColor().BLUE.color,
  fontSize: 18,
  fontWeight: 600,
  lineHeight: '20px',
  letterSpacing: '-0.36px',
}));

export const TryAgainDescription = styled('p')<{ isJoinFlow: boolean }>(({ isJoinFlow }) => ({
  margin: 0,
  display: 'block',
  color: isJoinFlow ? colors.light.gray.light : getTextColor().LIGHT_GRAY.color,
  fontSize: 15,
  fontWeight: 400,
  lineHeight: 'normal',
  letterSpacing: '-0.3px',
  width: '100%',
  fontStyle: 'italic',
}));

export const UlContainer = styled('ul')({
  display: 'flex',
  flexDirection: 'column',
  gap: 10,
  paddingInlineStart: '22px',
});

export const customTextStyle = {
  color: colors.light.gray.light,
  fontSize: 15,
  fontWeight: 400,
  lineHeight: 'normal',
  letterSpacing: '-0.3px',
};

export const ListDescription = styled('li')<{ isJoinFlow: boolean }>(({ isJoinFlow }) => ({
  ...customTextStyle,
  color: isJoinFlow ? colors.light.gray.light : getTextColor().LIGHT_GRAY.color,
}));

export const helpContainerCustomStyle: React.CSSProperties = {
  flexDirection: 'column',
  textAlign: 'center',
  alignItems: 'center',
  padding: isMobile ? '20px 30px' : '20px 70px',
  marginTop: 20,
};

// @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
export const IDVerifiedButton = styled(Button)(() => ({
  borderRadius: '4px !important',
  width: '190px',
  marginTop: 20,
  fontSize: '15px',
  fontWeight: 600,
  lineHeight: 'normal',
  letterSpacing: '-0.3px',
  height: 49,
}));
